import React, { useState, useEffect, useRef } from 'react'
import Sidebar from './Sidebar'

const actionLookupTable = {
  make_offer: 'Offer or counteroffer made',
  request_payment_details: 'Payment details requested',
  request_settlement_agreement: 'Settlement agreement requested',
}

const stateAbbreviations = {
  'Alabama': 'AL',
  'Alaska': 'AK',
  'Arizona': 'AZ',
  'Arkansas': 'AR',
  'California': 'CA',
  'Colorado': 'CO',
  'Connecticut': 'CT',
  'Delaware': 'DE',
  'Florida': 'FL',
  'Georgia': 'GA',
  'Hawaii': 'HI',
  'Idaho': 'ID',
  'Illinois': 'IL',
  'Indiana': 'IN',
  'Iowa': 'IA',
  'Kansas': 'KS',
  'Kentucky': 'KY',
  'Louisiana': 'LA',
  'Maine': 'ME',
  'Maryland': 'MD',
  'Massachusetts': 'MA',
  'Michigan': 'MI',
  'Minnesota': 'MN',
  'Mississippi': 'MS',
  'Missouri': 'MO',
  'Montana': 'MT',
  'Nebraska': 'NE',
  'Nevada': 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  'Ohio': 'OH',
  'Oklahoma': 'OK',
  'Oregon': 'OR',
  'Pennsylvania': 'PA',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  'Tennessee': 'TN',
  'Texas': 'TX',
  'Utah': 'UT',
  'Vermont': 'VT',
  'Virginia': 'VA',
  'Washington': 'WA',
  'West Virginia': 'WV',
  'Wisconsin': 'WI',
  'Wyoming': 'WY',
  'District of Columbia': 'DC',
}

const Notifications = () => {
  const [policies, setPolicies] = useState([])
  const [actions, setActions] = useState([])
  const [states, setStates] = useState([])
  const [newPolicy, setNewPolicy] = useState({
    email_type: '',
    state: [],
    emails: ['', ''],
  })
  const [isActionOpen, setIsActionOpen] = useState(false)
  const [isStateOpen, setIsStateOpen] = useState(false)
  const actionDropdownRef = useRef(null)
  const stateDropdownRef = useRef(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [actionsResponse, statesResponse, policiesResponse] = await Promise.all([
          fetch('/collectors/api/get_notification_policy_actions'),
          fetch('/collectors/api/get_notification_policy_states'),
          fetch('/collectors/api/get_notification_policies'),
        ])

        if (actionsResponse.ok && statesResponse.ok && policiesResponse.ok) {
          const actionsData = await actionsResponse.json()
          const statesData = await statesResponse.json()
          const policiesData = await policiesResponse.json()

          setActions(actionsData)
          setStates(statesData)
          setPolicies(
            policiesData.map(policy => ({
              ...policy,
              emails: policy.addresses.split(';'),
            }))
          )
        } else {
          console.error('Error fetching data')
        }
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    const handleClickOutside = event => {
      if (actionDropdownRef.current && !actionDropdownRef.current.contains(event.target)) {
        setIsActionOpen(false)
      }
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
        setIsStateOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const formatActionText = action => {
    if (action === '*') return 'All'
    return actionLookupTable[action]
  }

  const formatStateText = state => {
    if (Array.isArray(state)) {
      if (state.length === 0) return 'Select States'
      if (state.includes('*')) return 'All'
      if (state.length > 0) {
        return state.map(s => stateAbbreviations[s] || s).join(', ')
      }
    }
    // Handle semi-colon separated string
    if (typeof state === 'string' && state.includes(';')) {
      return state
        .split(';')
        .map(s => s.trim())
        .map(s => stateAbbreviations[s] || s)
        .join(', ')
    }
    return state === '*' ? 'All' : stateAbbreviations[state] || state
  }

  const handleEmailChange = (index, value) => {
    const updatedEmails = [...newPolicy.emails]
    updatedEmails[index] = value

    // Add new empty field if all existing fields are non-empty
    if (updatedEmails.every(email => email.trim() !== '')) {
      updatedEmails.push('')
    }

    setNewPolicy({
      ...newPolicy,
      emails: updatedEmails,
    })
  }

  const handleRemoveEmail = index => {
    const updatedEmails = newPolicy.emails.filter((_, i) => i !== index)
    // Ensure we maintain minimum 2 email fields
    while (updatedEmails.length < 2) {
      updatedEmails.push('')
    }
    setNewPolicy({
      ...newPolicy,
      emails: updatedEmails,
    })
  }

  const handleSubmit = async e => {
    e.preventDefault()

    const filteredEmails = newPolicy.emails.map(email => email.trim()).filter(email => email !== '')

    if (filteredEmails.length === 0) {
      alert('Please enter at least one email address')
      return
    }

    try {
      const csrfToken = document.querySelector('[name="csrf-token"]').content
      const response = await fetch('/collectors/api/add_notification_policy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          ...newPolicy,
          email_type:
            !newPolicy.email_type || newPolicy.email_type === 'All' ? '*' : newPolicy.email_type,
          state: !newPolicy.state.length || newPolicy.state.includes('*') ? ['*'] : newPolicy.state,
          emails: filteredEmails,
        }),
      })

      if (response.ok) {
        const newPolicyData = await response.json() // Get the response with the new policy ID
        const newPolicy = newPolicyData.policy
        setPolicies([
          ...policies,
          {
            ...newPolicy,
            id: newPolicy.id, // Use the ID from the server
            emails: filteredEmails,
          },
        ])
        setNewPolicy({ email_type: '', state: [], emails: ['', ''] })
      } else {
        alert('Failed to add policy. Please try again.')
      }
    } catch (error) {
      console.error('Error adding notification policy:', error)
      alert('Failed to add policy. Please try again.')
    }
  }

  const handleDelete = async policyId => {
    if (!confirm('Are you sure you want to delete this notification policy?')) {
      return
    }

    try {
      const csrfToken = document.querySelector('[name="csrf-token"]').content
      const response = await fetch(`/collectors/api/notification_policies/${policyId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
      })

      if (response.ok) {
        // Remove the deleted policy from the state
        setPolicies(policies.filter(policy => policy.id !== policyId))
      } else {
        console.error('Error deleting policy')
        alert('Failed to delete policy. Please try again.')
      }
    } catch (error) {
      console.error('Error deleting policy:', error)
      alert('Failed to delete policy. Please try again.')
    }
  }

  const Chevron = () => (
    <svg
      width='14'
      height='10'
      viewBox='0 0 14 10'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className='chevron-icon'
    >
      <path d='M13 1.6875L7 7.6875L1 1.6875' stroke='#9E9E9E' strokeWidth='2' />
    </svg>
  )

  return (
    <div className='container-fluid'>
      <div className='row'>
        <Sidebar />
        <div className='col mb-10'>
          <div className='d-flex justify-content-between align-items-center mb-4'>
            <h1>Manage Notifications</h1>
          </div>

          <p>We recommend setting at least 2 recipients per event.</p>

          {/* Display existing policies or empty state */}
          <div className='mb-8' style={{ maxWidth: '800px' }}>
            {policies.length > 0 ? (
              <>
                <div className='row align-items-start mb-2'>
                  <div className='col-md-4'>
                    <strong className='fs-6'>Event</strong>
                  </div>
                  <div className='col-md-3'>
                    <strong className='fs-6'>State / Territory</strong>
                  </div>
                  <div className='col-md-4'>
                    <strong className='fs-6'>Recipient Emails</strong>
                  </div>
                  <div className='col-md-1'></div>
                </div>
                {policies.map((policy, index) => (
                  <div key={policy.id || index} className='row align-items-start mb-3'>
                    <div className='col-md-4'>
                      <div className='form-control bg-light text-nowrap overflow-hidden fs-6'>
                        {formatActionText(policy.email_type)}
                      </div>
                    </div>
                    <div className='col-md-3'>
                      <div className='form-control bg-light fs-6'>
                        {formatStateText(policy.state)}
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <div className='form-control bg-light fs-6'>{policy.emails.join(', ')}</div>
                    </div>
                    <div className='col-md-1'>
                      <span
                        className='ms-2 d-flex align-items-center'
                        onClick={() => handleDelete(policy.id)}
                        style={{
                          cursor: 'pointer',
                          fontSize: '1.5rem',
                          lineHeight: 1,
                          height: '46px',
                        }}
                        aria-label='Delete policy'
                      >
                        ×
                      </span>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className='py-4 text-muted'>No recipients added yet</div>
            )}
          </div>

          <h3>Add new recipients</h3>

          {/* Form for adding new policy */}
          <div className='mb-4' style={{ maxWidth: '800px' }}>
            <form onSubmit={handleSubmit}>
              <div className='row align-items-start'>
                <div className='col-md-4'>
                  <div className='text-muted small mb-1'>When this occurs:</div>
                  <div className='dropdown' ref={actionDropdownRef}>
                    <button
                      type='button'
                      className={`btn dropdown-toggle w-100 fs-6 ${newPolicy.email_type ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setIsActionOpen(!isActionOpen)}
                      aria-expanded={isActionOpen}
                    >
                      <span className='d-flex align-items-center justify-content-between'>
                        {newPolicy.email_type
                          ? formatActionText(newPolicy.email_type)
                          : 'Select Action'}
                        <Chevron />
                      </span>
                    </button>

                    {isActionOpen && (
                      <div className='dropdown-menu show mt-1' style={{ minWidth: '200px' }}>
                        <div className='d-flex flex-column' style={{ maxHeight: '300px' }}>
                          <div className='overflow-auto flex-grow-1 p-3'>
                            <ul className='list-unstyled mb-0'>
                              {actions.map(action => (
                                <li key={action}>
                                  <div
                                    className='dropdown-item d-flex align-items-center'
                                    onClick={() => {
                                      setNewPolicy({ ...newPolicy, email_type: action })
                                      setIsActionOpen(false)
                                    }}
                                    role='button'
                                  >
                                    <span>{formatActionText(action)}</span>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='text-muted small mb-1'>In this location:</div>
                  <div className='dropdown' ref={stateDropdownRef}>
                    <button
                      type='button'
                      className={`btn dropdown-toggle w-100 fs-6 ${newPolicy.state.length > 0 ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setIsStateOpen(!isStateOpen)}
                      aria-expanded={isStateOpen}
                    >
                      <span className='d-flex align-items-center justify-content-between'>
                        {formatStateText(newPolicy.state)}
                        <Chevron />
                      </span>
                    </button>

                    {isStateOpen && (
                      <div className='dropdown-menu show mt-1' style={{ minWidth: '200px' }}>
                        <div className='d-flex flex-column' style={{ maxHeight: '300px' }}>
                          <div className='overflow-auto flex-grow-1 p-3'>
                            <ul className='list-unstyled mb-0'>
                              {states.map(state => (
                                <li key={state}>
                                  <div
                                    className='dropdown-item d-flex align-items-center'
                                    onClick={() => {
                                      if (state === '*') {
                                        setNewPolicy({ ...newPolicy, state: ['*'] })
                                      } else {
                                        const updatedStates = newPolicy.state.includes(state)
                                          ? newPolicy.state.filter(s => s !== state)
                                          : [...newPolicy.state.filter(s => s !== '*'), state]
                                        setNewPolicy({ ...newPolicy, state: updatedStates })
                                      }
                                    }}
                                    role='button'
                                  >
                                    <div className='form-check mb-0'>
                                      <input
                                        type='checkbox'
                                        className='form-check-input'
                                        checked={newPolicy.state.includes(state)}
                                        onChange={() => {}}
                                        style={{ cursor: 'pointer' }}
                                      />
                                    </div>
                                    <span className='ms-2'>{formatStateText(state)}</span>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className='d-flex gap-2 p-2 border-top'>
                            <button
                              className='btn btn-outline-secondary btn-sm'
                              onClick={() => {
                                setNewPolicy({ ...newPolicy, state: [] })
                                setIsStateOpen(false)
                              }}
                            >
                              Clear
                            </button>
                            <button
                              className='btn btn-primary btn-sm ms-auto'
                              onClick={() => setIsStateOpen(false)}
                            >
                              Done
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className='text-muted small mb-1'>Notify:</div>
                  {newPolicy.emails.map((email, index) => (
                    <div key={index} className='d-flex mb-2'>
                      <input
                        type='email'
                        className={`form-control me-2 ${index === 0 ? 'mt-0' : ''}`}
                        value={email}
                        onChange={e => handleEmailChange(index, e.target.value)}
                        placeholder={`Recipient email ${index + 1}`}
                        required={index === 0}
                      />
                      {newPolicy.emails.length > 2 && index >= 2 && (
                        <button
                          type='button'
                          className='btn btn-link text-secondary p-0'
                          onClick={() => handleRemoveEmail(index)}
                          style={{ minWidth: '24px' }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <div className='col-md-1'>
                  <div className='text-muted small mb-1'>&nbsp;</div>
                  <button type='submit' className='btn btn-primary'>
                    Add
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Notifications
